import React, { useState } from 'react';
import { Plus, Calendar, TrendingUp, AlertTriangle, Clock, Brain } from 'lucide-react';
import ComingSoonBanner from './common/ComingSoonBanner';
import { useSymptoms } from '../hooks/useSupabaseData';

export const SymptomTracking: React.FC = () => {
  const { symptoms, addSymptom, loading, error } = useSymptoms();
  const [showAddForm, setShowAddForm] = useState(false);

  const getSeverityColor = (severity: number) => {
    if (severity <= 3) return 'text-green-600 bg-green-50';
    if (severity <= 6) return 'text-yellow-600 bg-yellow-50';
    if (severity <= 8) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getSeverityLabel = (severity: number) => {
    if (severity <= 3) return 'Mild';
    if (severity <= 6) return 'Moderate';
    if (severity <= 8) return 'Severe';
    return 'Critical';
  };

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Smart Symptom Tracking"
        description="Your symptom tracker will integrate with your health devices, provide AI pattern analysis, correlate with treatments and lifestyle factors, and sync with your healthcare providers for comprehensive care."
        currentPage="/symptoms"
        variant="compact"
        expectedRelease="very soon"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src="/zippy-logo-simple.svg" 
              alt="ZippyHealth Logo"
              className="w-8 h-8"
            />
            <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Symptom Tracking
            </h2>
          </div>
          <p className="text-slate-600 mt-1">Monitor and track your symptoms with ZippyHealth.io's AI-powered insights</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105">
            <Brain className="h-4 w-4" />
            <span>AI Analysis</span>
          </button>
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            <span>Log Symptom</span>
          </button>
        </div>
      </div>

      {/* Quick Add Symptom Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Log New Symptom</h3>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target as HTMLFormElement);
            const newSymptom = {
              name: formData.get('name') as string,
              category: formData.get('category') as string,
              severity: parseInt(formData.get('severity') as string),
              notes: formData.get('notes') as string,
              triggers: (formData.get('triggers') as string).split(',').map(t => t.trim()).filter(t => t),
              recorded_at: new Date().toISOString()
            };
            const result = await addSymptom(newSymptom);
            if (result) {
              setShowAddForm(false);
              (e.target as HTMLFormElement).reset();
            }
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Symptom Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="e.g., Headache, Fatigue, Nausea"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <select
                  name="category"
                  required
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select category</option>
                  <option value="neurological">Neurological</option>
                  <option value="cardiovascular">Cardiovascular</option>
                  <option value="respiratory">Respiratory</option>
                  <option value="gastrointestinal">Gastrointestinal</option>
                  <option value="musculoskeletal">Musculoskeletal</option>
                  <option value="dermatological">Dermatological</option>
                  <option value="general">General</option>
                  <option value="mental_health">Mental Health</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Severity (1-10)</label>
                <input
                  type="range"
                  name="severity"
                  min="1"
                  max="10"
                  defaultValue="5"
                  className="w-full"
                  onInput={(e) => {
                    const output = e.target.parentElement?.querySelector('.severity-output');
                    if (output) output.textContent = (e.target as HTMLInputElement).value;
                  }}
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>Mild (1)</span>
                  <span className="severity-output font-medium">5</span>
                  <span>Severe (10)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Triggers (comma separated)</label>
                <input
                  type="text"
                  name="triggers"
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="e.g., stress, lack of sleep, certain foods"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Additional Notes</label>
              <textarea
                name="notes"
                rows={3}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Describe the symptom, when it started, what helps, etc."
              />
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Log Symptom
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading and Error States */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-700">Loading your symptoms...</p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading symptoms. Please try again.</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Today's Symptoms</p>
              <p className="text-2xl font-bold text-slate-800">2</p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">This Week</p>
              <p className="text-2xl font-bold text-slate-800">5</p>
            </div>
            <Calendar className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Average Severity</p>
              <p className="text-2xl font-bold text-slate-800">4.3</p>
            </div>
            <TrendingUp className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Severe Episodes</p>
              <p className="text-2xl font-bold text-slate-800">0</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* ZippyHealth AI Insights */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Brain className="h-6 w-6 text-red-600" />
          <h3 className="text-lg font-semibold text-slate-800">ZippyHealth AI Insights</h3>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-sm text-slate-700 mb-2">
            <strong>Pattern Detected:</strong> Your headaches seem to correlate with poor sleep quality and stress levels.
          </p>
          <p className="text-xs text-slate-600">
            Based on 3 recent entries, consider discussing sleep hygiene with your healthcare provider.
          </p>
        </div>
      </div>

      {/* Symptom Log */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-slate-800">Recent Symptoms</h3>
            <img 
              src="/favicon-96x96.png" 
              alt="Zippy" 
              className="w-4 h-4 opacity-60"
            />
          </div>
        </div>
        <div className="divide-y divide-slate-200">
          {symptoms.map((symptom) => (
            <div key={symptom.id} className="p-6 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-slate-800">{symptom.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(symptom.severity)}`}>
                      {getSeverityLabel(symptom.severity)} ({symptom.severity}/10)
                    </span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-slate-600 mb-2">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(symptom.recorded_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{new Date(symptom.recorded_at).toLocaleTimeString()}</span>
                    </div>
                    {symptom.category && (
                      <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full capitalize">
                        {symptom.category}
                      </span>
                    )}
                  </div>
                  {symptom.notes && (
                    <p className="text-sm text-slate-600 mb-2">{symptom.notes}</p>
                  )}
                  {symptom.triggers && symptom.triggers.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-slate-500">Triggers:</span>
                      {symptom.triggers.map((trigger, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                          {trigger}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    Edit
                  </button>
                  <button className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    Analyze
                  </button>
                </div>
              </div>
            </div>
          ))}
          {symptoms.length === 0 && !loading && (
            <div className="p-12 text-center">
              <Brain className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">No symptoms logged yet</h3>
              <p className="text-slate-600 mb-4">Start tracking your symptoms to identify patterns and improve your health.</p>
              <button 
                onClick={() => setShowAddForm(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Log Your First Symptom
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
